import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { Observable, BehaviorSubject } from "rxjs";
import { AdminService } from "../_services/admin.service";
import { Permissions } from "../_services/permissions";
import { Log } from "ng2-logger";
import 'rxjs/add/operator/filter';

declare const oidc: any;

const log = Log.create("OidcService");


@Injectable()
export class OidcService {
    private userManager?: any;
    private user = new BehaviorSubject<any>(null);

    user$: Observable<boolean> = this.user.asObservable();

    private settings = {
        authority: environment.OIDC_ISSUER,
        client_id: environment.OIDC_CLIENT_ID,
        redirect_uri: new URL("/?oidc-authenticate", window.location.origin).href,
        silent_redirect_uri: new URL("/?oidc-silent", window.location.origin).href,
        post_logout_redirect_uri: new URL("/", window.location.origin).href,
        response_type: "code",
        scope: "openid email",
        automaticSilentRenew: true,
        accessTokenExpiringNotificationTimeInSeconds: 120,
    }

    constructor(private adminService: AdminService, 
                private permissions: Permissions) {
        log.info("Initializing...");
        this.userManager = new oidc.UserManager(this.settings);

        // Handle general user changes, typically when issuing silent authentication requests
        // or token expirations
        this.userManager.events.addUserLoaded((user: any) => {
            log.info("oidc.userLoaded", user);
            if (user === null || user.expired) {
                this.signinRedirect();
                return;
            }
            this.setCurrentUser(user);
        });
    }

    private setCurrentUser(user: any) {
        (window as any).accessToken = user.access_token;
        this.adminService.aclFeatures()
            .subscribe((aclFeatures) => {
                this.permissions.features = aclFeatures;
                this.user.next(user);
            });
    }

    private unsetCurrentUser() {
        (window as any).accessToken = "";
        this.permissions.features = [];
        this.user.next(null);
    }

    private getUser(): Observable<any> {
        return Observable.from(this.userManager.getUser() as Promise<any>);
    }

    /**
     * Redirect the user to the login page.
     */
    signinRedirect() {
        this.userManager.signinRedirect();
    }

    /**
     * Redirect the user to the logout page. This is the function you should call if you want
     * to log the user out.
     */
    signoutRedirect() {
        this.userManager.signoutRedirect();
    }

    signinCallback(): Observable<any> {
        return Observable.from(this.userManager.signinCallback() as Promise<any>);
    }

    signinSilentCallback(): Observable<any> {
        return Observable.from(this.userManager.signinSilentCallback() as Promise<any>)
    }

    /**
     * This function should be called on application setup, with the only exception being endpoints
     * which manage the signin process. It automatically ensures we have a logged in user, by 
     * redirecting if this isn't the case, and sets up all the user data that the application
     * requires.
     */
    processUser() {
        this.getUser().subscribe((user) => {
            log.info("User changed", user);
            if (user === null) {
                this.signinRedirect();
                return;
            }
            if (user.expired) {
                this.signoutRedirect();
                return;
            }
            this.setCurrentUser(user);
        });
    }

    /**
     * This helper observer waits until a user is available before firing. It also only
     * fires once. If you want to listen in on all user changes, subscribe to the `user$`
     * observable instead.
     * 
     * @returns a single fire observable which returns a non-null user
     */
    getUserOnce(): Observable<any> {
        return Observable.create((observable) => {
            if (this.user.value !== null) {
                observable.next(this.user.value);
                observable.complete();
            } else {
                const subscription = this.user.subscribe(user => {
                    if (user !== null) {
                        observable.next(user);
                        observable.complete();
                    }
                });
                return () => subscription.unsubscribe();
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/auth/oidc.service.ts